import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Company, CompanyVendor, Vendor } from '../classes/models';
import { ZendeskTicket } from '../classes/models/zendesk-ticket';
import { ZendeskTicketComment } from '../classes/models/zendesk-ticket-comment';
import { ZendeskTicketField } from '../classes/models/zendesk-ticket-field';
import { ZendeskUser } from '../classes/models/zendesk-user';
import { TicketType } from '../interfaces/ticket-type';
import { ZendeskTicketCommentContent } from '../interfaces/zendesk-ticket-comment-content';

@Injectable({
  providedIn: 'root'
})
export class ZendeskTicketService {
  constructor(private http: HttpClient) {}

  createNewTicket(
    TicketInfo: {
      VendorId: number;
      CompanyId: number;
      InsuranceAgentId?: number;
      subject: string;
      description: string;
      ticketType: TicketType;
    },
    query = {}
  ) {
    const { ticketType } = TicketInfo;

    if (
      new Set([
        'default',
        'general',
        'vendorInfoReview',
        'ownerInfoReview',
        'tradeLicensesReview',
        'insuranceReview'
      ]).has(ticketType)
    ) {
      return this.http.post<any>('/api/v2/ticket', TicketInfo, {
        params: query
      });
    } else if (ticketType === 'newInsurance') {
      return this.http.post<any>('/api/v2/ticket/newInsurance', TicketInfo, {
        params: query
      });
    }

    throw new Error('Route has not been set up for ticket type.');
  }

  getZendeskTicket(query = {}) {
    return this.http.get<ZendeskTicket>('/api/v2/zendeskTicket/ticket/one', {
      params: query
    });
  }

  // Consider finding betters ways to pull ZD User with respect to security. Currently any user(s) can be pulled
  // from backend from any VIVE user. (Perhaps require TicketId & validate that the ticket id is related to Vendor/Client
  // User)
  getZendeskUser(
    params: { ZendeskTicketId?: number } = { ZendeskTicketId: undefined },
    query = {}
  ) {
    const { ZendeskTicketId } = params;
    const routeParts = [`/api/v2/zendeskTicket/`];
    if (ZendeskTicketId) {
      routeParts.push(`${ZendeskTicketId}/`);
    }
    routeParts.push(`user/one`);
    return this.http.get<ZendeskUser>(routeParts.join(''), { params: query });
  }

  getZendeskUsers(
    params: { ZendeskTicketId?: number } = { ZendeskTicketId: undefined },
    query = {}
  ) {
    const { ZendeskTicketId } = params;
    const routeParts = [`/api/v2/zendeskTicket/`];
    if (ZendeskTicketId) {
      routeParts.push(`${ZendeskTicketId}/`);
    }
    routeParts.push(`user/many`);
    return this.http.get<ZendeskUser[]>(routeParts.join(''), { params: query });
  }

  getZendeskTicketFields(ZendeskTicketId: number, query = {}) {
    return this.http.get<ZendeskTicketField[]>(
      `/api/v2/zendeskTicket/${ZendeskTicketId}/field/many`,
      { params: query }
    );
  }

  saveZendeskTicketCommentContent(
    ZendeskTicketId: number,
    ZendeskTicketComment: ZendeskTicketComment & {
      ZendeskTicketCommentContent: ZendeskTicketCommentContent & {
        Attachments: { filename: string; filetype: string; filedata: string }[];
      };
    },
    query = {}
  ) {
    return this.http.post<
      ZendeskTicketComment & {
        ZendeskTicketCommentContent: ZendeskTicketCommentContent & {
          ZendeskAuthorUser: ZendeskUser;
        };
      }
    >(
      `/api/v2/zendeskTicket/${ZendeskTicketId}/comment`,
      ZendeskTicketComment,
      { params: query }
    );
  }
}
